import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { Extraction } from "../../../api/knowledgeData/knowledgeDataDecoders";
import { processTerm } from "../../../api/content/processTerms";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { appPaths } from "../../../shared/routing/appPaths";
import { ItemReferenceName } from "../../items";
import { namedPerson } from "../../threads/data/namedPerson";
import { useMemberNames, type MemberNames } from "../../threads/data/useMemberNames";
import { useThreadOverview } from "../../threads/data/useThreadOverview";
import { ContentVersionName, labelOf, useVersionLabels } from "../../versions";
import { classText } from "../../workflows/inputCards/typeWords";
import { resultProvenance, type ProvenanceRow, type ProvenanceShown } from "./resultProvenance";

/**
 * The provenance of an extraction result: the Thread entry that holds it, the versions, LLM
 * and people of the step that produced it, and the curators who decided it (FR-DATA-3), each
 * by its name (F155): the Run's title and entry, the step's name and version label, the
 * review as the viewer's or a named person's. The recorded IDs are under Technical details.
 * Each statement's exact source is drawn on the text beside it.
 */
export default function ProvenancePanel({ extraction }: { extraction: Extraction }) {
  const { t } = useTranslation();
  const people = useMemberNames(extraction.projectId);
  const rows = resultProvenance(extraction);
  const items = rows.flatMap((row) => (row.shown?.kind === "item" ? [row.shown.reference] : []));
  const labels = useVersionLabels(extraction.projectId, { itemVersions: items, collectionVersions: [] });
  const label = (row: ProvenanceRow) => (row.key === "contentVersion" ? classText(processTerm("ContentVersion"), t) : t(`data.provenance.rows.${row.key}`));
  const named = rows.flatMap((row) => (row.shown === null ? [] : [{ row, shown: row.shown }]));
  // The first row names the Run; another entry of it is named by its number.
  const shownOf = (row: ProvenanceRow, shown: ProvenanceShown) => (
    <Shown shown={shown} projectId={extraction.projectId} runNamed={row.key === "threadEntry" ? null : extraction.threadId} people={people} versionOf={(reference) => labelOf(labels, reference)?.version ?? null} />
  );
  return (
    <section className="space-y-2" aria-label={t("data.provenance.title")}>
      <dl className="grid grid-cols-[minmax(7rem,9rem)_1fr] gap-x-3 gap-y-1 text-sm">
        {named.map(({ row, shown }, index) => (
          <Fragment key={`${row.key}-${String(index)}`}>
            <dt className="app-text-muted">{label(row)}</dt>
            <dd className="min-w-0">{shownOf(row, shown)}</dd>
          </Fragment>
        ))}
      </dl>
      <details>
        <summary className="app-link cursor-pointer text-sm">{t("data.abox.technical")}</summary>
        <dl className="mt-1 grid grid-cols-[minmax(7rem,9rem)_1fr] gap-x-3 gap-y-1 text-xs">
          {rows.map((row, index) => (
            <Fragment key={`${row.key}-${String(index)}`}>
              <dt className="app-text-muted">{label(row)}</dt>
              <dd className="min-w-0 break-all font-mono">{row.recorded}</dd>
            </Fragment>
          ))}
        </dl>
      </details>
      <p className="app-text-muted text-xs">{t("data.provenance.recordedOnly")}</p>
    </section>
  );
}

interface ShownProps {
  readonly shown: ProvenanceShown;
  readonly projectId: string;
  /** The Run already named, whose entries are named by number alone; `null`: none yet. */
  readonly runNamed: string | null;
  readonly people: MemberNames;
  readonly versionOf: (reference: string) => string | null;
}

function Shown({ shown, projectId, runNamed, people, versionOf }: ShownProps) {
  const { t, i18n } = useTranslation();
  switch (shown.kind) {
    case "entry":
      return <EntryName threadId={shown.threadId} entry={shown.entry} withRun={shown.threadId !== runNamed} />;
    case "item": {
      const version = versionOf(shown.reference);
      return <><ItemReferenceName reference={shown.reference} showVersion={false} />{version === null ? null : <span className="app-text-muted"> {version}</span>}</>;
    }
    case "modelVersion":
      return <ContentVersionName projectId={projectId} reference={shown.reference} />;
    case "person":
      return <>{people.nameOf(shown.userId)}</>;
    case "review":
      return <>{t("data.provenance.review", namedPerson(people, shown.reviewer))}</>;
    case "curation":
      return <>{people.nameOf(shown.curator)}, {t(`data.curation.kinds.${shown.decision}`)}</>;
    case "generation":
      return <>{t(`data.generation.${shown.generation}`)}</>;
    case "time":
      return <>{formatDateTime(shown.at, i18n.resolvedLanguage)}</>;
    case "text":
      return <>{shown.text}</>;
  }
}

/** A Run's entry, by the Run's title and the entry's number; `withRun` false: by number alone, in the Run already named. */
function EntryName({ threadId, entry, withRun }: { threadId: string; entry: number; withRun: boolean }) {
  return (
    <Link to={appPaths.thread(threadId, entry)} className="app-link">
      {withRun ? <RunEntryText threadId={threadId} entry={entry} /> : <EntryText entry={entry} />}
    </Link>
  );
}

function EntryText({ entry }: { entry: number }) {
  const { t } = useTranslation();
  return <>{t("data.provenance.entry", { entry })}</>;
}

function RunEntryText({ threadId, entry }: { threadId: string; entry: number }) {
  const { t } = useTranslation();
  const { state } = useThreadOverview(threadId, undefined);
  return state.status === "loaded" ? <>{t("data.provenance.runEntry", { run: state.value.title, entry })}</> : <EntryText entry={entry} />;
}
